import { useCallback } from "react";

import type { ReservationDetailIssueReceiptInput } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { RESERVATION_DETAIL_ISSUE_RECEIPT_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_MUTATION } from "./schema";
import * as Shared from "../../../../_shared";

export const useReservationDetailIssueReceiptMutation = () => {
	const { mutationFunc } = useCustomMutation(
		RESERVATION_DETAIL_ISSUE_RECEIPT_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_MUTATION,
	);
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onReservationDetailIssueReceipt = useCallback(
		async (input: ReservationDetailIssueReceiptInput): Promise<{ pdfUrl: string } | undefined> => {
			showLoadingScreenCallback();
			try {
				const { data, errors } = await mutationFunc({
					variables: { input },
					update: (cache, { data }) => {
						/**
						 * キャッシュから予約詳細を取得
						 */
						const cached = cache.readQuery({
							query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
							variables: { hotelID: input.hotelID, reservationId: input.reservationID },
						});
						/**
						 * レスポンスから更新されたステータスを取得
						 */
						const updated =
							data?.reservationDetailIssueReceipt.reservationDetail?.statusReceiptIssued;
						if (!cached || updated === undefined) return;

						/**
						 * 予約更新のキャッシュを更新
						 */
						cache.writeQuery({
							query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
							variables: { hotelID: input.hotelID, reservationId: input.reservationID },
							data: {
								reservationDetail: {
									...cached.reservationDetail,
									statusReceiptIssued: updated,
								},
								repeaterReservationList: cached.repeaterReservationList,
							},
						});
					},
				});

				if (!errors && data) {
					notifyUserEventInfo({
						variant: "default",
						title: "領収書を発行しました",
					});
					return { pdfUrl: data.reservationDetailIssueReceipt.pdfUrl };
				}
				return undefined;
			} finally {
				hideLoadingScreenCallback();
			}
		},
		[hideLoadingScreenCallback, mutationFunc, showLoadingScreenCallback],
	);

	return {
		onReservationDetailIssueReceipt,
	};
};
